(() => {
  if (customElements.get('tcs-boots-purchase')) return;
  customElements.define('tcs-boots-purchase', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.config = JSON.parse(this.querySelector('[data-boots-config]').textContent);
      this.form = this.querySelector('form');
      this.idInput = this.form.querySelector('[name=id]');
      this.idInput.disabled = false;
      this.quantityInput = this.form.querySelector('[name=quantity]');
      this.button = this.querySelector('#boots-add-to-bag');
      this.strings = this.config.strings;
      this.variants = this.config.variants;
      this.sizes = [...new Set(this.variants.map(v => v.size))];
      this.colours = [...new Set(this.variants.map(v => v.colour))];
      this.money = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency: this.config.currency });
      const selected = this.variants.find(v => v.id === this.config.selectedId) || this.variants[0];
      this.setPrimary(selected);
      this.sets = [{ size: selected.size, colour: selected.colour, sizeOverride: false, colourOverride: false }];
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.form.addEventListener('change', e => this.change(e), { signal });
      this.pickerUnsubscribe = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, payload => this.pickerChange(payload));
      this.form.addEventListener('submit', e => this.submit(e), { signal });
      this.querySelector('#boots-apply-main').addEventListener('click', () => {
        this.currencyFallback = false;
        this.sets = this.sets.map(() => ({ ...this.primary(), sizeOverride: false, colourOverride: false }));
        this.controls(); this.refresh();
      }, { signal });
      this.querySelector('#boots-fit-link').addEventListener('click', event => { event.preventDefault(); this.focusAt('#boots-fit-title'); }, { signal });
      this.querySelector('#boots-sticky-action').addEventListener('click', () => {
        this.form.scrollIntoView({ behavior: this.motion() });
        this.mainPicker().querySelector('input:checked')?.focus({ preventScroll: true });
      }, { signal });
      this.querySelector('.proof-link').addEventListener('click', e => {
        if (e.target.closest('a')) { e.preventDefault(); this.focusAt('#boots-reviews-title'); }
      }, { signal });
      this.stickyObserver = new ResizeObserver(() => {
        const height = this.querySelector('#boots-sticky-buy').getBoundingClientRect().height;
        document.body.style.setProperty('--tcs-boots-sticky-height', `${height}px`);
      });
      this.stickyObserver.observe(this.querySelector('#boots-sticky-buy'));
      window.addEventListener('popstate', () => {
        const id = Number(new URL(location.href).searchParams.get('variant'));
        const variant = this.variants.find(v => v.id === id);
        if (variant) { this.currencyFallback = false; this.setPrimary(variant); this.sets = this.sets.map(() => ({ ...this.primary(), sizeOverride: false, colourOverride: false })); this.controls(); this.refresh(false); this.variantPhoto(variant); }
      }, { signal });
      this.quotes = new Map();
      this.currencyReady = false;
      this.initialQuoteAttempts = 0;
      this.initialRetryTimer = null;
      this.purchaseVisible = false;
      this.observer = new IntersectionObserver(entries => {
        const ratio = entries[0].intersectionRatio;
        if (ratio >= .3) this.purchaseVisible = true;
        else if (ratio <= .05) this.purchaseVisible = false;
        this.querySelector('#boots-sticky-buy').hidden = !this.currencyReady || this.purchaseVisible;
      }, { threshold: [0, .05, .3] });
      this.observer.observe(this.button);
      this.quoteTimer = setInterval(() => { if (!document.hidden && !this.busy) this.refresh(false, true, false); }, 30000);
      document.addEventListener('visibilitychange', () => { if (!document.hidden && !this.busy) this.refresh(false, true, false); }, { signal });
      this.controls(); this.refresh(false);
    }
    disconnectedCallback() { clearInterval(this.quoteTimer); clearTimeout(this.quoteDebounce); clearTimeout(this.initialRetryTimer); this.priceRequest?.abort(); this.abort?.abort(); this.mediaRequest?.abort(); this.pickerUnsubscribe?.(); this.observer?.disconnect(); this.stickyObserver?.disconnect(); document.body.style.removeProperty('--tcs-boots-sticky-height'); this.ready = false; }
    motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }
    focusAt(selector) { const element = this.querySelector(selector); if (!element) return; element.tabIndex = -1; element.scrollIntoView({ behavior: this.motion() }); element.focus({ preventScroll: true }); }
    text(key, values = {}) { return Object.entries(values).reduce((text, [name, value]) => text.replaceAll(`[${name}]`, String(value)), this.strings[key] || ''); }
    mainPicker() { return this.querySelector('[data-boots-primary] variant-selects'); }
    pickerChoice(picker) {
      const choice = {};
      picker.getAllSelectedOptions().forEach(option => {
        const field = option.name.toLowerCase() === 'size' ? 'size' : 'colour';
        choice[field] = option.value;
      });
      return choice;
    }
    primary() { return this.pickerChoice(this.mainPicker()); }
    setPrimary(variant) { this.syncPicker(this.mainPicker(), variant); }
    syncPicker(picker, choice) {
      picker.querySelectorAll('input[type=radio]').forEach(input => {
        const field = input.dataset.optionName.toLowerCase() === 'size' ? 'size' : 'colour';
        input.checked = input.value === choice[field];
        const alternative = this.variant({ ...choice, [field]: input.value });
        input.classList.toggle('disabled', !alternative?.available);
        input.classList.toggle('visually-disabled', !alternative?.available && input.classList.contains('swatch-input__input'));
        const metadata = input.closest('.product-form__input').querySelector('[data-selected-value]');
        if (metadata && input.checked) metadata.textContent = input.value;
      });
      picker.querySelectorAll('select').forEach(select => {
        const field = select.options[0]?.dataset.optionName.toLowerCase() === 'size' ? 'size' : 'colour';
        [...select.options].forEach(option => {
          option.selected = option.value === choice[field];
          option.toggleAttribute('selected', option.selected);
        });
        picker.updateSelectionMetadata({ target: select });
      });
      const variant = this.variant(choice);
      picker.dataset.selectedPriceAmount = ((variant?.price || 0) / 100).toFixed(2);
      const selected = picker.querySelector('[data-selected-variant]');
      if (selected) selected.textContent = JSON.stringify(variant ? { ...variant, title: `${variant.colour} / ${variant.size}` } : null);
      picker.resolvePendingSelectPromise(variant ? { ...variant, title: `${variant.colour} / ${variant.size}` } : null);
    }
    pickerChange({ data: { event, target } }) {
      const picker = event.target.closest('variant-selects');
      if (!picker || !this.contains(picker) || this.busy) return;
      const choice = this.pickerChoice(picker);
      if (picker === this.mainPicker()) {
        this.currencyFallback = false;
        this.resize();
        this.sets.forEach(item => {
          ['size', 'colour'].forEach(field => { if (!item[`${field}Override`]) item[field] = choice[field]; });
        });
        this.controls(); this.refresh(); this.variantPhoto(this.variant(choice));
      } else {
        this.currencyFallback = false;
        const index = Number(picker.closest('[data-set-index]').dataset.setIndex);
        const item = this.sets[index]; if (!item) return;
        const field = target.dataset.optionName.toLowerCase() === 'size' ? 'size' : 'colour';
        item[field] = choice[field]; item[`${field}Override`] = choice[field] !== this.primary()[field];
        this.controls(); this.refresh(false);
      }
    }
    count() { return Number(this.form.elements['boots-sets'].value); }
    variant(item) { return this.variants.find(v => v.size === item.size && v.colour === item.colour); }
    resize() {
      const n = this.count(), primary = this.primary();
      if (n === 1) { this.sets = [{ ...primary, sizeOverride: false, colourOverride: false }]; return; }
      while (this.sets.length < n) this.sets.push({ ...primary, sizeOverride: false, colourOverride: false });
      this.sets = this.sets.slice(0, n);
    }
    change(e) {
      if (e.target.name !== 'boots-sets') return;
      this.currencyFallback = false;
      this.resize(); this.controls(); this.refresh();
    }
    controls() {
      const count = this.count();
      this.querySelector('#boots-set-customisation').hidden = count === 1;
      if (count === 1) this.querySelector('#boots-set-disclosure').open = false;
      this.querySelectorAll('[data-set-index]').forEach(row => {
        const index = Number(row.dataset.setIndex); row.hidden = index >= count;
        this.syncPicker(row.querySelector('variant-selects'), this.sets[index] || this.primary());
      });
      this.syncPicker(this.mainPicker(), this.primary());
    }
    items() {
      const counts = new Map();
      this.sets.forEach(item => { const v = this.variant(item); if (v) counts.set(v.id, (counts.get(v.id) || 0) + 1); });
      return [...counts].map(([id, quantity]) => ({ id, quantity }));
    }
    refresh(updateURL = true, requestQuote = true, clearError = true) {
      const primary = this.primary(), n = this.count(), v = this.variant(primary);
      if (v) {
        this.idInput.value = v.id;
        if (updateURL) { const url = new URL(location.href); url.searchParams.set('variant', v.id); history.replaceState({}, '', url); }
      }
      this.quantityInput.value = n;
      const requests = [...this.form.querySelectorAll('[name=boots-sets]')].map(radio => ({ count: Number(radio.value), items: v ? [{ id: v.id, quantity: Number(radio.value) }] : [] }));
      const activeItems = this.items();
      const activeEntry = this.quoteEntryFor(activeItems);
      const quoted = activeEntry?.currency === this.config.currency ? activeEntry.value : null;
      const gross = this.sets.reduce((sum, item) => sum + (this.variant(item)?.price || 0), 0);
      const total = quoted?.total ?? gross;
      const tierEntries = requests.map(request => this.quoteEntryFor(request.items));
      const tierQuotes = tierEntries.map(entry => entry?.currency === this.config.currency ? entry.value : null);
      requests.forEach((request, index) => {
        const entry = tierEntries[index], currencyConfirmed = (entry?.expires > Date.now() && entry.currency === this.config.currency) || this.currencyFallback;
        const priceCell = this.querySelector(`[data-offer-total="${request.count}"]`).closest('.offer-price');
        priceCell.classList.toggle('is-price-pending', !currencyConfirmed);
        const quote = tierQuotes[index], amount = quote?.total ?? (v?.price || 0) * request.count;
        if (currencyConfirmed) {
          this.querySelector(`[data-offer-total="${request.count}"]`).textContent = this.money.format(amount / 100);
          this.querySelector(`[data-offer-unit="${request.count}"]`).textContent = this.money.format(amount / request.count / 100);
        }
        const savings = this.querySelector(`[data-offer-savings="${request.count}"]`);
        const percentage = quote?.gross > 0 ? Math.floor((quote.gross - quote.total) * 100 / quote.gross + 0.000001) : 0;
        savings.classList.toggle('has-savings', percentage > 0);
        savings.textContent = percentage > 0 ? this.text('saving', { percentage }) : '';
        const badge = this.querySelector(`[data-offer-lowest="${request.count}"]`);
        if (badge) badge.classList.toggle('is-lowest', !!quote && percentage > 0 && tierQuotes.every(Boolean) && requests.every((other, i) => quote.total / request.count <= tierQuotes[i].total / other.count));
      });
      const groups = [...requests.map(x => x.items), activeItems].filter(items => items.length);
      const pending = groups.some(items => !this.quoteCurrencyReady(items));
      this.querySelector('.offer-field').setAttribute('aria-busy', String(pending));
      if (requestQuote) this.scheduleQuotes(requests.map(x => x.items).concat([activeItems]));
      const choices = [...new Set(this.sets.map(item => `${item.size} · ${item.colour}`))];
      const summaryReady = quoted || activeEntry?.currency === this.config.currency || this.currencyFallback;
      const summary = this.text('summary', { choice: choices.length === 1 ? choices[0] : this.strings.mixed, count: n, unit: n === 1 ? this.strings.set : this.strings.sets, boots: n * 4, total: this.money.format(total / 100) });
      const purchaseSummary = this.querySelector('#boots-purchase-summary'), stickySelection = this.querySelector('#boots-sticky-selection');
      purchaseSummary.classList.toggle('is-price-pending', !summaryReady);
      stickySelection.classList.toggle('is-price-pending', !summaryReady);
      if (summaryReady) { purchaseSummary.textContent = summary; stickySelection.textContent = summary; }
      this.querySelector('#boots-sticky-action').textContent = n === 1 ? this.strings.chooseOne : this.strings.chooseMany;
      this.querySelector('#boots-selection-live').textContent = activeEntry?.currency === this.config.currency || this.currencyFallback ? `${summary}. ${this.sets.map((item, index) => `${this.text('setLabel', { count: index + 1 })}: ${item.size}, ${item.colour}`).join('. ')}` : '';
      if (!this.busy) this.querySelector('[data-add-label]').textContent = n === 1 ? this.strings.one : this.text('many', { count: n });
      const unavailable = this.sets.filter(item => !this.variant(item)?.available);
      const limits = this.items().filter(item => { const variant = this.variants.find(v => v.id === item.id); return variant.tracked && item.quantity > variant.inventory; });
      const notices = unavailable.map(item => this.text('unavailable', { choice: `${item.colour} / ${item.size}` })).concat(limits.map(item => { const variant = this.variants.find(v => v.id === item.id); return this.text('limited', { choice: `${variant.colour} / ${variant.size}`, count: variant.inventory }); }));
      this.querySelector('#boots-availability').textContent = [...new Set(notices)].join(' ');
      this.button.disabled = !!this.busy || unavailable.length > 0 || !this.currencyReady;
      this.form.querySelectorAll('[data-set-index]').forEach(control => { const item = this.sets[Number(control.dataset.setIndex)]; const invalid = !!item && !this.variant(item)?.available; control.setAttribute('aria-invalid', String(invalid)); if (invalid) control.setAttribute('aria-describedby', 'boots-availability'); else control.removeAttribute('aria-describedby'); });
      if (clearError) this.error();
    }
    quoteKey(items) { return JSON.stringify([this.config.country, this.config.currency, [...items].sort((a, b) => a.id - b.id)]); }
    quoteEntryFor(items) { const entry = this.quotes?.get(this.quoteKey(items)); return entry?.expires > Date.now() ? entry : null; }
    quoteFor(items) { const entry = this.quoteEntryFor(items); return entry?.currency === this.config.currency ? entry.value : null; }
    quoteCurrencyReady(items) { return this.quoteEntryFor(items)?.currency === this.config.currency; }
    quotePriceReady(items) {
      const entry = this.quoteEntryFor(items);
      return entry?.currency === this.config.currency && !!entry.value;
    }
    initialQuoteGroups() {
      const primary = this.primary(), variant = this.variant(primary);
      const groups = [...this.form.querySelectorAll('[name="boots-sets"]')].map(radio => variant ? [{ id: variant.id, quantity: Number(radio.value) }] : []);
      groups.push(this.items());
      return [...new Map(groups.filter(items => items.length).map(items => [this.quoteKey(items), items])).values()];
    }
    revealPrices(fallback = false) {
      if (this.currencyReady) return;
      const groups = this.initialQuoteGroups();
      if (!fallback && !groups.every(items => this.quotePriceReady(items))) return;
      this.currencyFallback = fallback;
      this.currencyReady = true;
      this.refresh(false, false, false);
      this.classList.remove('is-initial-price-pending');
      this.querySelector('.offer-field').setAttribute('aria-busy', 'false');
      this.querySelector('.boots-picker-shell').setAttribute('aria-busy', 'false');
      this.button.setAttribute('aria-busy', 'false');
      const intersection = this.button.getBoundingClientRect();
      const visibleHeight = Math.max(0, Math.min(intersection.bottom, window.innerHeight) - Math.max(intersection.top, 0));
      const ratio = visibleHeight / intersection.height;
      if (ratio >= .3) this.purchaseVisible = true;
      else if (ratio <= .05) this.purchaseVisible = false;
      this.querySelector('#boots-sticky-buy').hidden = this.purchaseVisible;
    }
    scheduleQuotes(groups) {
      clearTimeout(this.quoteDebounce);
      this.priceRequest?.abort();
      const missing = [...new Map(groups.filter(items => items.length).map(items => [this.quoteKey(items), items])).entries()].filter(([key]) => !(this.quotes?.get(key)?.expires > Date.now()));
      if (!missing.length) return;
      this.quoteDebounce = setTimeout(() => this.fetchQuotes(missing), 200);
    }
    async fetchQuotes(missing) {
      const controller = this.priceRequest = new AbortController();
      const declarations = missing.map((_, i) => `$input${i}: CartInput!`).join(', ');
      const fields = missing.map((_, i) => `offer${i}: cartCreate(input: $input${i}) { userErrors { message } warnings { code } cart { totalQuantity cost { totalAmount { currencyCode } } lines(first: 4) { nodes { quantity merchandise { ... on ProductVariant { id } } cost { subtotalAmount { amount currencyCode } totalAmount { amount currencyCode } } } } } }`).join(' ');
      const variables = Object.fromEntries(missing.map(([, items], i) => [`input${i}`, { lines: items.map(item => ({ merchandiseId: `gid://shopify/ProductVariant/${item.id}`, quantity: item.quantity })), buyerIdentity: { countryCode: this.config.country } }]));
      try {
        const response = await fetch('/api/2026-10/graphql.json', { method: 'POST', credentials: 'omit', cache: 'no-store', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ query: `mutation BootsOffers(${declarations}) { ${fields} }`, variables }), signal: AbortSignal.any([controller.signal, AbortSignal.timeout(10000)]) });
        if (!response.ok) throw new Error('Offer quote unavailable');
        const result = await response.json();
        if (controller.signal.aborted) return;
        missing.forEach(([key, items], i) => {
          const offer = result.data?.[`offer${i}`], lines = offer?.cart?.lines.nodes;
          let value = null;
          const currency = offer?.cart?.cost?.totalAmount?.currencyCode || null;
          if (!result.errors?.length && !offer?.userErrors?.length && !offer?.warnings?.length && lines?.length === items.length && offer.cart.totalQuantity === items.reduce((sum, item) => sum + item.quantity, 0)) {
            const valid = lines.every(line => items.some(item => line.merchandise.id === `gid://shopify/ProductVariant/${item.id}` && line.quantity === item.quantity) && [line.cost.subtotalAmount, line.cost.totalAmount].every(money => money.currencyCode === this.config.currency && Number.isFinite(Number(money.amount)) && Number(money.amount) >= 0));
            if (valid) {
              const gross = lines.reduce((sum, line) => sum + Math.round(Number(line.cost.subtotalAmount.amount) * 100), 0), total = lines.reduce((sum, line) => sum + Math.round(Number(line.cost.totalAmount.amount) * 100), 0);
              const expected = items.reduce((sum, item) => sum + this.variants.find(v => v.id === item.id).price * item.quantity, 0);
              if (gross === expected && total <= gross) value = { gross, total };
            }
          }
          this.quotes.set(key, { value, currency, expires: Date.now() + (currency === this.config.currency ? 30000 : 10000) });
        });
      } catch {
        if (controller.signal.aborted) return;
        missing.forEach(([key]) => this.quotes.set(key, { value: null, currency: null, expires: Date.now() + 10000 }));
      }
      if (!controller.signal.aborted && this.isConnected) {
        if (this.quotes.size > 100) this.quotes = new Map([...this.quotes].filter(([, entry]) => entry.expires > Date.now()));
        this.refresh(false, false, false);
        if (!this.currencyReady) {
          const initial = this.initialQuoteGroups();
          if (initial.every(items => this.quotePriceReady(items))) this.revealPrices();
          else if (this.initialQuoteAttempts < 2) {
            this.initialQuoteAttempts++;
            const missingInitial = initial.filter(items => !this.quotePriceReady(items));
            missingInitial.forEach(items => this.quotes.delete(this.quoteKey(items)));
            clearTimeout(this.initialRetryTimer);
            this.initialRetryTimer = setTimeout(() => this.scheduleQuotes(missingInitial), this.initialQuoteAttempts === 1 ? 500 : 1200);
          } else this.revealPrices(true);
        }
      }
    }
    async variantPhoto(variant) {
      this.mediaRequest?.abort();
      if (!variant?.featuredMediaId) return;
      const request = this.mediaRequest = new AbortController();
      const sectionId = this.dataset.sectionId;
      try {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        url.searchParams.set('section_id', sectionId);
        const response = await fetch(url, { signal: request.signal });
        if (!response.ok) throw new Error(`Gallery response ${response.status}`);
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (request.signal.aborted) return;
        const source = html.querySelector('tcs-boots-purchase media-gallery');
        const modalSource = html.querySelector('tcs-boots-purchase product-modal .product-media-modal__content');
        if (!source) throw new Error('Native gallery missing from response');
        HTMLUpdateUtility.setInnerHTML(this.querySelector('[data-boots-gallery-content]'), source.outerHTML);
        const modal = document.getElementById(`ProductModal-${sectionId}`)?.querySelector('.product-media-modal__content');
        if (modal && modalSource) HTMLUpdateUtility.setInnerHTML(modal, modalSource.innerHTML);
        this.querySelector('media-gallery')?.setActiveMedia?.(`${sectionId}-${variant.featuredMediaId}`, false);
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Boots native gallery update:', error);
      }
    }
    error(message = '', review = false) {
      const box = this.querySelector('#boots-form-error'); box.hidden = !message;
      box.querySelector('[data-error-text]').textContent = message;
      box.querySelector('[data-review-cart]').hidden = !review;
    }
    async cartRead() {
      const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store', signal: AbortSignal.timeout(15000) });
      if (!response.ok) throw new Error('Cart read failed');
      const cart = await response.json(); if (!Array.isArray(cart.items) || !cart.currency) throw new Error('Invalid cart'); return cart;
    }
    counts(cart) { const counts = new Map(); cart.items.forEach(item => counts.set(item.variant_id, (counts.get(item.variant_id) || 0) + item.quantity)); return counts; }
    standardEvent(items) {
      const Event = window.StandardEvents?.CartLinesUpdateEvent; if (!Event) return null;
      const pending = Event.createPromise();
      this.dispatchEvent(new Event({ action: 'add', context: 'product', lines: items.map(item => ({ merchandiseId: String(item.id), quantity: item.quantity })), promise: pending.promise }));
      return pending;
    }
    async showCart(cart, response, items, message = '') {
      const ui = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      if (typeof publish === 'function') {
        try { await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', productVariantId: items[0].id, cartData: cart }); } catch { }
      }
      if (!ui) { window.location.assign(routes.cart_url); return; }
      const ids = ui.getSectionsToRender().map(section => section.id);
      let sections = response?.sections;
      if (!sections || ids.some(id => typeof sections[id] !== 'string')) {
        const query = new URLSearchParams({ sections: ids.join(',') });
        const refresh = await fetch(`${routes.cart_url}?${query}`, { signal: AbortSignal.timeout(15000) });
        if (!refresh.ok) throw new Error('Cart sections failed'); sections = await refresh.json();
      }
      const line = cart.items.find(line => items.some(item => item.id === line.variant_id));
      if (!line || ids.some(id => typeof sections[id] !== 'string')) throw new Error('Missing cart section');
      const parser = new DOMParser();
      const selectors = ui.matches('cart-drawer') ? { 'cart-drawer': '#CartDrawer', 'cart-icon-bubble': '.shopify-section' } : { 'cart-notification-product': `[id="cart-notification-product-${line.key}"]`, 'cart-notification-button': '.shopify-section', 'cart-icon-bubble': '.shopify-section' };
      if (ids.some(id => !parser.parseFromString(sections[id], 'text/html').querySelector(selectors[id] || '.shopify-section'))) throw new Error('Invalid cart section HTML');
      ui.setActiveElement(this.button); ui.classList.toggle('is-empty', cart.item_count === 0);
      ui.renderContents({ ...cart, id: line.variant_id, key: line.key, sections });
      if (message && ui.matches('cart-drawer')) {
        const notice = document.createElement('p'); notice.className = 'tcs-boots-cart-limit'; notice.setAttribute('role', 'status'); notice.textContent = message;
        ui.querySelector('.drawer__header')?.after(notice);
      }
    }
    async submit(event) {
      event.preventDefault(); if (this.busy || this.button.disabled) return;
      const items = this.items(); if (!items.length || items.reduce((sum, item) => sum + item.quantity, 0) !== this.count()) return;
      if (items.some(item => { const v = this.variants.find(v => v.id === item.id); return item.quantity < v.min || item.quantity % v.increment !== 0; })) { this.error(this.strings.rules); return; }
      this.busy = true; this.button.disabled = true; this.button.setAttribute('aria-busy', 'true'); this.querySelector('[data-add-label]').textContent = this.strings.adding;
      const controls = [...this.form.querySelectorAll('input[type=radio], select, #boots-apply-main')].map(control => ({ control, disabled: control.disabled }));
      controls.forEach(({ control }) => { control.disabled = true; });
      this.error(); let before, after, response, status = 0, uncertain = false, pending, announcement = '';
      try {
        try { before = await this.cartRead(); } catch { this.error(this.strings.beforeFailed); return; }
        const ui = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const sections = ui?.getSectionsToRender().map(section => section.id) || [];
        pending = this.standardEvent(items);
        try {
          const result = await fetch(routes.cart_add_url, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items, sections, sections_url: location.pathname }), signal: AbortSignal.timeout(20000) });
          status = result.status; response = await result.json();
        } catch { uncertain = true; }
        try { after = await this.cartRead(); } catch { }
        if (!after) { pending?.reject(new Error('Cart confirmation unavailable')); this.error(this.strings.uncertain, true); return; }
        const previous = this.counts(before), current = this.counts(after);
        const added = items.reduce((sum, item) => sum + Math.max(0, (current.get(item.id) || 0) - (previous.get(item.id) || 0)), 0);
        const requested = items.reduce((sum, item) => sum + item.quantity, 0);
        const success = !uncertain && status >= 200 && status < 300 && !response?.status && added >= requested;
        let message = '';
        if (uncertain) message = this.strings.uncertain;
        else if (added > 0 && !success) message = this.text('partial', { added, requested });
        else if (!success) message = response?.description || response?.message || this.strings.failed;
        if (success) {
          const Event = window.StandardEvents?.CartLinesUpdateEvent;
          if (pending && Event) pending.resolve({ cart: Event.createCartFromAjaxResponse(after) });
        } else {
          pending?.reject(new Error(message));
          const ErrorEvent = window.StandardEvents?.CartErrorEvent;
          if (ErrorEvent) this.dispatchEvent(new ErrorEvent({ error: message, code: uncertain ? 'SERVICE_UNAVAILABLE' : 'INVALID' }));
        }
        if (added > 0 || success) {
          try { await this.showCart(after, response, items, message); }
          catch { this.error(message || this.text('added', { count: added }), true); }
        }
        if (message) this.error(message, true);
        if (success) announcement = this.text('added', { count: added });
      } finally {
        this.busy = false; this.button.removeAttribute('aria-busy');
        const message = this.querySelector('[data-error-text]').textContent, review = !this.querySelector('[data-review-cart]').hidden;
        controls.forEach(({ control, disabled }) => { control.disabled = disabled; });
        this.refresh(false); if (message) this.error(message, review);
        if (announcement) this.querySelector('#boots-selection-live').textContent = announcement;
      }
    }
  });
})();
